import type { DesignTokens } from "../types/theme";

// Space Academy brand font: Hanken Grotesk (bundled in apps/web), with a robust
// system fallback chain so text stays readable if the webfont is slow/offline.
const WEB_SYSTEM_FONT =
  "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Base tokens for Light mode — Space Academy "Observatorio": warm paper +
 * cobalt/ultramarine accent + amber signal. Values mirror packages/ui/vars.css
 * (WCAG AA verified pairs).
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f8f7f4",
  foreground: "#1b212b",
  surface: "#ffffff",
  surfaceForeground: "#1b212b",
  surfaceSecondary: "#f1f0ed",
  surfaceSecondaryForeground: "#1b212b",
  surfaceTertiary: "#e9e8e4",
  surfaceTertiaryForeground: "#1b212b",
  overlay: "#ffffff",
  overlayForeground: "#1b212b",
  muted: "#5d646f",
  default: "#eeede9",
  defaultForeground: "#1b212b",
  accent: "#204faa",
  accentForeground: "#ffffff",
  danger: "#b1262a",
  dangerForeground: "#ffffff",
  warning: "#ebb34b",
  warningForeground: "#1b212b",
  success: "#157c4f",
  successForeground: "#ffffff",
  separator: "#c1c4cb",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(0, 0, 0, 0.1)",
  glassHighlight: "rgba(0, 0, 0, 0.08)",
  glow0: "rgba(32, 79, 170, 0.07)",
  glow1: "rgba(235, 179, 75, 0.06)",
  glow2: "rgba(44, 125, 167, 0.05)",
  mutedForeground: "#1b212b",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(27, 33, 43, 0.08)",
  overlayShadow: "rgba(27, 33, 43, 0.14)",
  fieldShadow: "rgba(27, 33, 43, 0.05)",
  fieldBorder: "rgba(0, 0, 0, 0.1)",
  fieldPlaceholder: "rgba(0, 0, 0, 0.4)",
  fieldBackground: "rgba(0, 0, 0, 0.03)",
  segment: "#ffffff",
  segmentForeground: "#1b212b",
  link: "#1b212b",
  focus: "#204faa",
  fieldForeground: "#1b212b",
  radius: "0.5rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#c1c4cb",
  primary: "#204faa",
  primaryForeground: "#ffffff",
  secondary: "#f1f0ed",
  secondaryForeground: "#1b212b",
  backdrop: "rgba(11, 14, 21, 0.45)",
};

/**
 * Base tokens for Dark mode — deep space-navy (never pure black) + luminous
 * cobalt with dark ink foreground.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#0b0e15",
  foreground: "#f2f0ec",
  surface: "#151a22",
  surfaceForeground: "#f2f0ec",
  surfaceSecondary: "#1e232c",
  surfaceSecondaryForeground: "#f2f0ec",
  surfaceTertiary: "#282d36",
  surfaceTertiaryForeground: "#f2f0ec",
  overlay: "#191e27",
  overlayForeground: "#f2f0ec",
  muted: "#9ea5b2",
  default: "#232831",
  defaultForeground: "#f2f0ec",
  accent: "#83abf4",
  accentForeground: "#0b0e15",
  danger: "#f17070",
  dangerForeground: "#0b0e15",
  warning: "#eebb58",
  warningForeground: "#0b0e15",
  success: "#63c992",
  successForeground: "#0b0e15",
  separator: "#3d434d",
  glassBackground: "rgba(11, 14, 21, 0.985)",
  glassBorder: "rgba(255, 255, 255, 0.12)",
  glassHighlight: "rgba(255, 255, 255, 0.25)",
  glow0: "rgba(131, 171, 244, 0.16)",
  glow1: "rgba(238, 187, 88, 0.1)",
  glow2: "rgba(44, 125, 167, 0.12)",
  mutedForeground: "#f2f0ec",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.3)",
  overlayShadow: "rgba(0, 0, 0, 0.5)",
  fieldShadow: "rgba(0, 0, 0, 0.25)",
  fieldBorder: "rgba(255, 255, 255, 0.1)",
  fieldPlaceholder: "rgba(255, 255, 255, 0.4)",
  fieldBackground: "rgba(255, 255, 255, 0.03)",
  segment: "#323843",
  segmentForeground: "#f2f0ec",
  link: "#f2f0ec",
  focus: "#83abf4",
  fieldForeground: "#f2f0ec",
  radius: "0.5rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#3d434d",
  primary: "#83abf4",
  primaryForeground: "#0b0e15",
  secondary: "#232831",
  secondaryForeground: "#f2f0ec",
  backdrop: "rgba(0, 0, 0, 0.65)",
};
